import React from "react";
import { LuCheck, LuExternalLink, LuTag } from "react-icons/lu";

import MaterialPhoto from "../materialLines/MaterialPhoto";
import cx from "../ui/cx";
import { formatLength, rateSourceLabel } from "../../static/cuttingLine";
import { formatAmount, sanitizeQty } from "../../static/materialLine";
import { sanitizeRate } from "../../static/processRate";
import { route } from "../../static/route";
import { CuttingRateSource } from "../../types/cuttingOrder";

// The editor rows of a card: a label with its box on the right; the box gives way on a narrow (phone) card.
export const rowClass = "flex items-center justify-between gap-4";

export const rowLabelClass = "shrink-0 text-xs font-medium text-gray-600";

export const rowBoxClass = "relative w-28 min-w-0";

export const cardInputClass =
  "h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-right text-sm font-semibold placeholder:text-muted-foreground focus:border-[#113F67] focus:outline-none focus:ring-1 focus:ring-[#113F67]";

// The rate box, like the material picker's price box: the qty box with an icon in front (a tag, as on the order form).
const cardRateClass = cx(
  cardInputClass,
  "pl-8 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
);

type RateRowProps = {
  // The box's name for screen readers, "CB-02 ભાવ".
  label: string;
  value: string;
  disabled?: boolean;
  // Why it can't be changed.
  title?: string;
  onChange: (rate: string) => void;
};

// The editor's "ભાવ:" row.
export const CardRateRow = ({
  label,
  value,
  disabled,
  title,
  onChange,
}: RateRowProps) => (
  <label className={rowClass}>
    <span className={rowLabelClass}>ભાવ:</span>
    <span className={rowBoxClass}>
      <LuTag className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        aria-label={label}
        title={title}
        placeholder="0.00"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(sanitizeRate(event.target.value))}
        className={cardRateClass}
      />
    </span>
  </label>
);

type QtyRowProps = {
  // The box's name for screen readers, "CB-02 જથ્થો".
  label: string;
  value: string;
  inputRef?: React.Ref<HTMLInputElement>;
  onChange: (qty: string) => void;
};

// The editor's "જથ્થો:" row.
export const CardQtyRow = ({ label, value, inputRef, onChange }: QtyRowProps) => (
  <label className={rowClass}>
    <span className={rowLabelClass}>જથ્થો:</span>
    <span className={rowBoxClass}>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        aria-label={label}
        value={value}
        onChange={(event) => onChange(sanitizeQty(event.target.value))}
        className={cardInputClass}
      />
    </span>
  </label>
);

const itemEditPath = (itemId: number) =>
  route.itemEdit.replace(":id", itemId as unknown as string);

type Props = {
  code: string;
  // The size after the code, on a card of one size.
  sizeName: string | null;
  // The parent's code for a sub item, else the item's description.
  subtitle: string | null;
  imageUrl: string | null;
  // The page whose item holds it (a sub item is edited on its parent's page), linked for a user who may edit items.
  editItemId: number;
  canOpenItem: boolean;
  materialName: string | null;
  // The cut length after the material (none on a card of more sizes), or "લંબાઈ સેટ નથી" when the item has none.
  cutLength: number | null;
  lengthMissing: boolean;
  sizeUnit: string;
  // The rate and where it came from, only for a user who may see rates.
  rate?: number;
  rateSource?: CuttingRateSource;
  // More rows at the end of the top part.
  extra?: React.ReactNode;
  // Picked: its editor shows, with the green border and ✓ of the material picker's ticked card (user, 2026-10-31). Its
  // line goes on the form once the editor has a qty.
  isOpen: boolean;
  canToggle: boolean;
  // Why the card can't be ticked or unticked.
  title?: string;
  onToggle: () => void;
  // The editor under the top part, while the card is open.
  editor?: React.ReactNode;
};

// A card of the cutting pickers, copied from the material picker's card: the photo with its tick, the code, the
// subtitle, the material with the cut length and the rate; clicking the top ticks or unticks it, and its editor shows
// under it. Its sizes and spacing (the odd px values too) were matched to the user's reference cards.
const CuttingPickerCard = ({
  code,
  sizeName,
  subtitle,
  imageUrl,
  editItemId,
  canOpenItem,
  materialName,
  cutLength,
  lengthMissing,
  sizeUnit,
  rate,
  rateSource,
  extra,
  isOpen,
  canToggle,
  title,
  onToggle,
  editor,
}: Props) => (
  <div
    className={cx(
      "overflow-hidden rounded-lg border bg-white",
      isOpen ? "border-green-600 ring-1 ring-green-600" : "border-gray-200",
      lengthMissing && !isOpen && "opacity-70"
    )}
  >
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isOpen}
      aria-disabled={!canToggle}
      title={title}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle();
        }
      }}
      className={cx(
        "block p-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#113F67]",
        canToggle ? "cursor-pointer" : "cursor-default"
      )}
    >
      <div className="relative">
        <MaterialPhoto
          name={code}
          imageUrl={imageUrl}
          className="aspect-[4/3] w-full rounded-md"
        />
        <span
          aria-hidden="true"
          className={cx(
            "absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full",
            isOpen ? "bg-green-600 text-white" : "bg-white shadow-sm"
          )}
        >
          {isOpen && <LuCheck className="h-3.5 w-3.5" strokeWidth={3} />}
        </span>
      </div>

      {/* The size of a one-size card follows the code, and gets a line of its own under it when both don't fit. */}
      <div className="mt-2 flex items-start gap-1 text-sm font-semibold leading-6 text-[#113F67]">
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-1">
          <span className="truncate" title={code}>
            {code}
          </span>
          {sizeName && (
            <span className="text-xs text-gray-600">{`(${sizeName})`}</span>
          )}
        </span>
        {canOpenItem && (
          <a
            href={itemEditPath(editItemId)}
            target="_blank"
            rel="noopener noreferrer"
            title="આઈટમ જુઓ"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className="mt-[4.5px] shrink-0 hover:opacity-70"
          >
            <LuExternalLink className="h-[15px] w-[15px]" />
          </a>
        )}
      </div>

      <div
        className="h-4 truncate text-xs leading-4 text-gray-500"
        title={subtitle || undefined}
      >
        {subtitle}
      </div>

      {/* The material, with the cut length of a one-size card; a card with more sizes picks them in its editor. */}
      <div className="mt-1 flex items-center justify-between gap-2 text-xs leading-5">
        <span className="truncate text-gray-600" title={materialName || undefined}>
          {materialName || "-"}
        </span>
        {lengthMissing ? (
          <span className="shrink-0 font-semibold text-red-600">
            લંબાઈ સેટ નથી
          </span>
        ) : (
          cutLength !== null && (
            <span className="shrink-0 font-semibold text-[#113F67]">
              {formatLength(cutLength, sizeUnit)}
            </span>
          )
        )}
      </div>

      {rate !== undefined && (
        <div className="flex items-center justify-between gap-2 text-xs leading-5">
          <span className="text-gray-500">{rateSourceLabel(rateSource)}</span>
          <span className="font-semibold text-gray-700">
            ₹{formatAmount(rate)}
          </span>
        </div>
      )}

      {extra}
    </div>

    {editor && (
      <div className="space-y-2.5 border-t border-gray-200 bg-gray-50 px-2.5 pb-2.5 pt-[13px]">
        {editor}
      </div>
    )}
  </div>
);

export default CuttingPickerCard;
